import { z, ZodType } from 'zod';
import { customerNameRegex, cityRegex, zipFormatRegex } from '../../../../../constants/constant';
import { mobileNumberPattern, customerDetailsSchemaMessages, numericRegex } from '../constants';
import { CustomerDetailsData } from '../types';
import { getCardType } from '../utils';

// Reusable validation functions
let cardType = 'unknown';
const stringField = (regex: RegExp, minLength: number, maxLength: number, messageMin: string, messageMax: string) => {
  return z
    .string()
    .min(minLength, { message: messageMin })
    .regex(regex, messageMax)
    .max(maxLength, { message: messageMax });
};

const optionalStringField = (regex: RegExp, minLength: number, maxLength: number, message: string) => {
  return z.string().regex(regex, message).min(minLength, { message }).max(maxLength, { message }).optional();
};

const phoneNumberValidation = (phoneNumberPattern: RegExp) => {
  return z
    .string()
    .optional()
    .refine((value) => value === '' || phoneNumberPattern.test(value as any), { message: 'Invalid Phone Number' });
};

// Reusable field definitions
const firstName = stringField(
  customerNameRegex,
  1,
  40,
  customerDetailsSchemaMessages.firstNameRequired,
  customerDetailsSchemaMessages.firstNameInvalid,
);

const lastName = stringField(
  customerNameRegex,
  1,
  40,
  customerDetailsSchemaMessages.lastNameRequired,
  customerDetailsSchemaMessages.lastNameInvalid,
);
const companyName = optionalStringField(
  /^.{0,40}$/,
  0,
  40,
  customerDetailsSchemaMessages.companyNameInvalid,
).optional();
const streetName = z
  .string()
  .min(1, { message: customerDetailsSchemaMessages.addressLine1Required })
  .max(30, { message: customerDetailsSchemaMessages.addressInvalid })
  .regex(/^[a-zA-Z0-9\s/#.,;:'\-\/\u00B0]+$/, {
    message: customerDetailsSchemaMessages.addressInvalid,
  })
  .refine(
    (value) => {
      if (value) {
        return /^[a-zA-Z0-9\s/#.,;:'\-\u00B0]+$/.test(value);
      }
      return true;
    },
    { message: 'Invalid character' },
  )
  .transform((value) => value.trim())
  .optional();
const city = stringField(
  cityRegex,
  1,
  30,
  customerDetailsSchemaMessages.cityRequired,
  customerDetailsSchemaMessages.cityInvalid,
);
const state = stringField(
  /^.{1,50}$/,
  1,
  50,
  customerDetailsSchemaMessages.stateRequired,
  customerDetailsSchemaMessages.stateRequired,
);
const postalCode = z
  .string()
  .min(1, { message: customerDetailsSchemaMessages.zipRequired })
  .refine(
    (value) => {
      if (!zipFormatRegex.test(value)) {
        return false;
      }
      const sanitizedValue = value.replace(/\D/g, '');
      const isValidLength = sanitizedValue.length === 5 || (sanitizedValue.length >= 9 && sanitizedValue.length <= 10);
      const isNotAllZeros = !/^0+$/.test(sanitizedValue);
      return isValidLength && isNotAllZeros;
    },
    { message: customerDetailsSchemaMessages.postalCodeInvalid },
  )
  .optional();
const phone = phoneNumberValidation(mobileNumberPattern);

// Helper functions to support schema validations
const isNonEmptyString = (value: string) => value.trim().length > 0;

const isValidCardNumber = (value: string) => {
  const cleanedValue = value.replace(/\D/g, '');
  return cleanedValue.length === 15 || cleanedValue.length === 16;
};

const isValidCardType = (value: string) => {
  const cleanedValue = value.replace(/\D/g, '');
  cardType = getCardType(cleanedValue).toString();
  return cardType !== 'unknown';
};

const isValidMonth = (value: string) => {
  const [monthStr] = value.split('/');
  const month = parseInt(monthStr, 10);
  return !isNaN(month) && month >= 1 && month <= 12;
};

const isExpiryDateInFuture = (value: string) => {
  const [monthStr, yearStr] = value.split('/');
  const currentDate = new Date();
  const month = parseInt(monthStr, 10);
  const year = parseInt(yearStr, 10) + 2000;
  const expiryDate = new Date(year, month - 1, 1);
  return expiryDate > currentDate;
};

const isValidCVV = (value: string) => {
  const cleanedValue = value.replace(/\D/g, '');
  const length = cleanedValue.length;
  if (value === '0000' || value === '000') return false;
  switch (cardType.toLowerCase()) {
    case 'visa':
    case 'mastercard':
    case 'discover':
      return length === 3;
    case 'americanexpress':
      return length === 4;
    default:
      return false;
  }
};

const requiredPhoneNumberValidation = (phoneNumberPattern: RegExp) => {
  return z
    .string()
    .refine((value) => phoneNumberPattern.test(value), { message: customerDetailsSchemaMessages.phoneNumberInavlid });
};

// Schema composition
const baseAddressSchema = z.object({
  firstName,
  lastName,
  companyName,
  streetName,
  city,
  state,
  postalCode,
});

export const customerDetailsSchema: ZodType<CustomerDetailsData> = baseAddressSchema.extend({
  email: z.string().email(customerDetailsSchemaMessages.emailInvalid).optional(),
  additionalAddressInfo: z
    .string()
    .min(0)
    .max(30)
    .refine(
      (value) => {
        if (value) {
          return /^[a-zA-Z0-9\s/#.,;:'\-\u00B0]+$/.test(value);
        }
        return true;
      },
      { message: customerDetailsSchemaMessages.addressInvalid },
    )
    .optional(),
  phone: z.string().optional(),
  ssn: z
    .string()
    .regex(numericRegex, customerDetailsSchemaMessages.ssnInvalid)
    .min(4, { message: customerDetailsSchemaMessages.ssnInvalid })
    .max(4, { message: customerDetailsSchemaMessages.ssnInvalid }),
  dob: z.string().refine(
    (value) => {
      value = value?.replaceAll('-', '/');
      if (!/^(19|20)\d{2}\/((0[1-9])|(1[0-2]))\/((0[1-9])|([12][0-9])|(3[01]))$/.test(value)) {
        return false;
      }
      const [year, month, day] = value.split('/').map(Number);
      const dobDate = new Date(year, month - 1, day);

      if (isNaN(dobDate.getTime())) {
        return false;
      }

      const lastDay = new Date(year, month, 0).getDate();
      if (day > lastDay) {
        return false;
      }

      const currentDate = new Date();
      return dobDate < currentDate;
    },
    { message: 'Please enter valid Date of Birth' },
  ),
  mailReturnUndeliverable: z.boolean().optional(),
});

export const customerNoCreditSchema: ZodType<CustomerDetailsData> = baseAddressSchema.extend({
  email: z.string().email(customerDetailsSchemaMessages.emailInvalid).optional(),
  additionalAddressInfo: z
    .string()
    .min(0)
    .max(30)
    .refine(
      (value) => {
        if (value) {
          return /^[a-zA-Z0-9\s/#.,;:'\-\u00B0]+$/.test(value);
        }
        return true;
      },
      { message: customerDetailsSchemaMessages.addressInvalid },
    )
    .optional(),
  phone: z.string().optional(),
  ssn: z.string().optional(),
  dob: z
    .string()
    .optional()
    .refine(
      (value) => {
        if (!value) return true;
        const normalized = value.replaceAll('-', '/');
        const regex = /^(19|20)\d{2}\/(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])$/;
        if (!regex.test(normalized)) {
          return false;
        }
        const [year, month, day] = normalized.split('/').map(Number);
        const dobDate = new Date(year, month - 1, day);

        if (isNaN(dobDate.getTime())) {
          return false;
        }
        const lastDayOfMonth = new Date(year, month, 0).getDate();
        if (day > lastDayOfMonth) {
          return false;
        }
        return dobDate < new Date();
      },
      {
        message: 'Please enter a valid Date of Birth',
      },
    ),
  mailReturnUndeliverable: z.boolean().optional(),
});

const createCustomerAddressBaseSchema = z.object({
  firstName: z
    .string()
    .min(1, { message: customerDetailsSchemaMessages.firstNameRequired })
    .regex(/^[a-zA-Z0-9\s-]*$/, customerDetailsSchemaMessages.firstNameInvalid)
    .max(40, { message: customerDetailsSchemaMessages.firstNameInvalid }),
  lastName: z
    .string()
    .min(1, { message: customerDetailsSchemaMessages.lastNameRequired })
    .regex(/^[a-zA-Z0-9\s-]*$/, customerDetailsSchemaMessages.lastNameInvalid)
    .max(40, { message: customerDetailsSchemaMessages.lastNameInvalid }),
  companyName,
  streetName,
  city: city,
  state,
  postalCode,
});

export const createCustomerSchema: ZodType<CustomerDetailsData> = createCustomerAddressBaseSchema.extend({
  email: z
    .string()
    .optional()
    .refine((val) => !val || z.string().email().safeParse(val).success, {
      message: 'Please enter a valid email address',
    }),
  additionalAddressInfo: z
    .string()
    .min(0, { message: customerDetailsSchemaMessages.additionalAddressInfo })
    .max(30, { message: customerDetailsSchemaMessages.addressInvalid })
    .refine(
      (value) => {
        if (value) {
          return /^[a-zA-Z0-9\s/#.,;:'\-\u00B0]+$/.test(value);
        }
        return true;
      },
      { message: customerDetailsSchemaMessages.addressInvalid },
    )
    .optional(),
  phone: requiredPhoneNumberValidation(mobileNumberPattern),
});

export const AddressSchema: ZodType<CustomerDetailsData> = baseAddressSchema.extend({
  additionalAddressInfo: z.string().min(0).max(30).optional(),
  phone: z
    .string()
    .optional()
    .refine(
      (value) => {
        if (!value) return true;
        return value.replace(/\D/g, '')?.length >= 10;
      },
      {
        message: customerDetailsSchemaMessages?.phoneInvalid,
      },
    )
    .refine(
      (value) => {
        if (!value) return true;
        return !/^0+$/.test(value.replace(/\D/g, ''));
      },
      {
        message: customerDetailsSchemaMessages?.invalidPhoneNumberMessage,
      },
    ),
});

export const paymentSchema: ZodType<any> = z.object({
  cardNumber: z
    .string()
    .min(1, { message: 'Enter a credit card number' })
    .refine(isNonEmptyString, { message: 'Enter a credit card number.' })
    .refine(isValidCardNumber, { message: 'Enter a valid credit card number' })
    .refine(isValidCardType, { message: 'Enter a valid credit card number' })
    .optional(),
  expDate: z
    .string()
    .refine(isNonEmptyString, { message: 'Enter expiry date.' })
    .refine(isValidMonth, { message: 'Invalid month' })
    .refine(isExpiryDateInFuture, { message: 'Expiry date must be in future' })
    .optional(),
  cvv: z
    .string()
    .min(1, { message: 'Enter a security code' })
    .refine(isValidCVV, { message: 'Enter a valid security code' })
    .optional(),
  paymentAmount: z.string(),
  billingAddress: z.string(),
  additonalOtherAmount: z
    .string()
    .refine((value) => isNonEmptyString(value || ''), { message: 'Enter a valid amount.' })
    .optional(),
});

export const managePhoneEmailSchema: ZodType<any> = z.object({
  phone: phone.optional(),
  email: z.string().email(customerDetailsSchemaMessages.emailInvalid).optional(),
});

export const AdjsutPaymentSchema: ZodType<any> = z.object({
  cardNumber: z
    .string()
    .min(1, { message: 'Enter a credit card number' })
    .refine(isNonEmptyString, { message: 'Enter a credit card number.' })
    .refine(isValidCardNumber, { message: 'Enter a valid credit card number' })
    .refine(isValidCardType, { message: 'Enter a valid credit card number' })
    .optional(),
  expDate: z
    .string()
    .refine(isNonEmptyString, { message: 'Enter expiry date.' })
    .refine(isValidMonth, { message: 'Invalid month' })
    .refine(isExpiryDateInFuture, { message: 'Expiry date must be in future' })
    .optional(),
  cvv: z
    .string()
    .min(1, { message: 'Enter a security code' })
    .refine(isValidCVV, { message: 'Enter a valid security code' })
    .optional(),
  billingAddress: z.string(),
  note: z.string().min(1, { message: 'Enter a note' }),
});

export const customerDetailsSSNSchema: ZodType<CustomerDetailsData> = baseAddressSchema.extend({
  email: z.string().email(customerDetailsSchemaMessages.emailInvalid).optional(),
  additionalAddressInfo: z
    .string()
    .min(0)
    .max(30)
    .refine(
      (value) => {
        if (value) {
          return /^[a-zA-Z0-9\s/#.,;:'\-\u00B0]+$/.test(value);
        }
        return true;
      },
      { message: customerDetailsSchemaMessages.addressInvalid },
    )
    .optional(),
  phone: z.string(),
  ssn: z
    .string()
    .regex(numericRegex, customerDetailsSchemaMessages.ssnInvalid)
    .min(4, { message: customerDetailsSchemaMessages.ssnInvalid })
    .max(4, { message: customerDetailsSchemaMessages.ssnInvalid }),
  dob: z
    .string()
    .optional()
    .refine(
      (value) => {
        if (!value) return true;
        const normalized = value.replaceAll('-', '/');
        const regex = /^(19|20)\d{2}\/(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])$/;
        if (!regex.test(normalized)) {
          return false;
        }
        const [year, month, day] = normalized.split('/').map(Number);
        const dobDate = new Date(year, month - 1, day);

        if (isNaN(dobDate.getTime())) {
          return false;
        }
        const lastDayOfMonth = new Date(year, month, 0).getDate();
        if (day > lastDayOfMonth) {
          return false;
        }
        return dobDate < new Date();
      },
      {
        message: 'Please enter a valid Date of Birth',
      },
    ),
  mailReturnUndeliverable: z.boolean().optional(),
});

export const customerDetailsDOBSchema: ZodType<CustomerDetailsData> = baseAddressSchema.extend({
  email: z.string().email(customerDetailsSchemaMessages.emailInvalid).optional(),
  additionalAddressInfo: z
    .string()
    .min(0)
    .max(30)
    .refine(
      (value) => {
        if (value) {
          return /^[a-zA-Z0-9\s/#.,;:'\-\u00B0]+$/.test(value);
        }
        return true;
      },
      { message: customerDetailsSchemaMessages.addressInvalid },
    )
    .optional(),
  phone: z.string(),
  ssn: z.string().optional(),
  dob: z.string().refine(
    (value) => {
      value = value?.replaceAll('-', '/');
      if (!/^(19|20)\d{2}\/((0[1-9])|(1[0-2]))\/((0[1-9])|([12][0-9])|(3[01]))$/.test(value)) {
        return false;
      }
      const [year, month, day] = value.split('/').map(Number);
      const dobDate = new Date(year, month - 1, day);

      if (isNaN(dobDate.getTime())) {
        return false;
      }

      const lastDay = new Date(year, month, 0).getDate();
      if (day > lastDay) {
        return false;
      }

      const currentDate = new Date();
      return dobDate < currentDate;
    },
    { message: 'Please enter valid Date of Birth' },
  ),
  mailReturnUndeliverable: z.boolean().optional(),
});

export const customerDetailsNoSSNSchema: ZodType<CustomerDetailsData> = baseAddressSchema.extend({
  email: z.string().email(customerDetailsSchemaMessages.emailInvalid).optional(),
  additionalAddressInfo: z
    .string()
    .min(0)
    .max(30)
    .refine(
      (value) => {
        if (value) {
          return /^[a-zA-Z0-9\s/#.,;:'\-\u00B0]+$/.test(value);
        }
        return true;
      },
      { message: customerDetailsSchemaMessages.addressInvalid },
    )
    .optional(),
  phone: z.string(),
  ssn: z.string().optional(),
  dob: z.string().refine(
    (value) => {
      if (!value) return true;
      const normalized = value.replaceAll('-', '/');
      const regex = /^(19|20)\d{2}\/(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])$/;
      if (!regex.test(normalized)) {
        return false;
      }
      const [year, month, day] = normalized.split('/').map(Number);
      const dobDate = new Date(year, month - 1, day);

      if (isNaN(dobDate.getTime())) {
        return false;
      }
      const lastDayOfMonth = new Date(year, month, 0).getDate();
      if (day > lastDayOfMonth) {
        return false;
      }
      return dobDate < new Date();
    },
    {
      message: 'Please enter a valid Date of Birth',
    },
  ),
  mailReturnUndeliverable: z.boolean().optional(),
});

export const customerDetailsNoDOBSchema: ZodType<CustomerDetailsData> = baseAddressSchema.extend({
  email: z.string().email(customerDetailsSchemaMessages.emailInvalid).optional(),
  additionalAddressInfo: z
    .string()
    .min(0)
    .max(30)
    .refine(
      (value) => {
        if (value) {
          return /^[a-zA-Z0-9\s/#.,;:'\-\u00B0]+$/.test(value);
        }
        return true;
      },
      { message: customerDetailsSchemaMessages.addressInvalid },
    )
    .optional(),
  phone: z.string(),
  ssn: z.string(),
  dob: z
    .string()
    .optional()
    .refine(
      (value) => {
        if (!value) return true;
        const normalized = value.replaceAll('-', '/');
        const regex = /^(19|20)\d{2}\/(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])$/;
        if (!regex.test(normalized)) {
          return false;
        }
        const [year, month, day] = normalized.split('/').map(Number);
        const dobDate = new Date(year, month - 1, day);

        if (isNaN(dobDate.getTime())) {
          return false;
        }
        const lastDayOfMonth = new Date(year, month, 0).getDate();
        if (day > lastDayOfMonth) {
          return false;
        }
        return dobDate < new Date();
      },
      {
        message: 'Please enter a valid Date of Birth',
      },
    ),
  mailReturnUndeliverable: z.boolean().optional(),
});
